import type { Permission } from "@/types";

export const TOUR_ANCHOR = {
  appNav: "app-nav",
  dashboardKpis: "dashboard-kpis",
  newProject: "new-project",
  memberProjects: "member-projects",
  globalSearch: "global-search",
  settings: "settings",
  accountMenu: "account-menu",
} as const;

export type TourAnchor = (typeof TOUR_ANCHOR)[keyof typeof TOUR_ANCHOR];

export type TourStepId =
  | "appNav"
  | "dashboardKpis"
  | "newProject"
  | "memberProjects"
  | "globalSearch"
  | "settings"
  | "accountMenu";

export type ResolvedTourStep = {
  id: TourStepId;
  anchor: TourAnchor;
  titleKey: string;
  descriptionKey: string;
};

type TourStepDef = {
  id: TourStepId;
  anchor: TourAnchor;
  titleKey: string;
  descriptionKey: string;
  requiredPermission?: Permission;
  whenDashboard?: boolean;
  whenMemberHome?: boolean;
};

const APP_HOME_STEPS: readonly TourStepDef[] = [
  {
    id: "appNav",
    anchor: TOUR_ANCHOR.appNav,
    titleKey: "productTour.steps.appNav.title",
    descriptionKey: "productTour.steps.appNav.description",
  },
  {
    id: "dashboardKpis",
    anchor: TOUR_ANCHOR.dashboardKpis,
    titleKey: "productTour.steps.dashboardKpis.title",
    descriptionKey: "productTour.steps.dashboardKpis.description",
    requiredPermission: "nav.dashboard",
    whenDashboard: true,
  },
  {
    id: "newProject",
    anchor: TOUR_ANCHOR.newProject,
    titleKey: "productTour.steps.newProject.title",
    descriptionKey: "productTour.steps.newProject.description",
    requiredPermission: "projects.create",
  },
  {
    id: "memberProjects",
    anchor: TOUR_ANCHOR.memberProjects,
    titleKey: "productTour.steps.memberProjects.title",
    descriptionKey: "productTour.steps.memberProjects.description",
    whenMemberHome: true,
  },
  {
    id: "globalSearch",
    anchor: TOUR_ANCHOR.globalSearch,
    titleKey: "productTour.steps.globalSearch.title",
    descriptionKey: "productTour.steps.globalSearch.description",
  },
  {
    id: "settings",
    anchor: TOUR_ANCHOR.settings,
    titleKey: "productTour.steps.settings.title",
    descriptionKey: "productTour.steps.settings.description",
    requiredPermission: "nav.settings",
  },
  {
    id: "accountMenu",
    anchor: TOUR_ANCHOR.accountMenu,
    titleKey: "productTour.steps.accountMenu.title",
    descriptionKey: "productTour.steps.accountMenu.description",
  },
];

export function resolveAppHomeTourSteps(options: {
  can: (permission: Permission) => boolean;
  presentAnchors?: ReadonlySet<string>;
}): ResolvedTourStep[] {
  const hasDashboard = options.can("nav.dashboard");

  return APP_HOME_STEPS.filter((step) => {
    if (step.whenDashboard && !hasDashboard) return false;
    if (step.whenMemberHome && hasDashboard) return false;
    if (step.requiredPermission && !options.can(step.requiredPermission)) return false;
    if (options.presentAnchors && !options.presentAnchors.has(step.anchor)) return false;
    return true;
  }).map((step) => ({
    id: step.id,
    anchor: step.anchor,
    titleKey: step.titleKey,
    descriptionKey: step.descriptionKey,
  }));
}

function isElementVisible(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return false;
  const style = getComputedStyle(el);
  if (style.visibility === "hidden" || style.display === "none" || style.opacity === "0") {
    return false;
  }
  return true;
}

export function findVisibleTourAnchor(anchor: string): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const nodes = document.querySelectorAll<HTMLElement>(`[data-tour="${anchor}"]`);
  for (const node of nodes) {
    if (isElementVisible(node)) return node;
  }
  return null;
}

export function collectPresentTourAnchors(): Set<string> {
  const present = new Set<string>();
  for (const anchor of Object.values(TOUR_ANCHOR)) {
    if (findVisibleTourAnchor(anchor)) present.add(anchor);
  }
  return present;
}

export function waitForTourAnchor(anchor: string, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = (): void => {
      if (findVisibleTourAnchor(anchor) || Date.now() - started >= timeoutMs) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}
